import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MapPin, List, Clock, ClipboardList, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LoadingState from '@/components/shared/LoadingState';
import EmptyState from '@/components/shared/EmptyState';
import TodaySummaryCard from '@/components/visits/TodaySummaryCard';
import VisitCard from '@/components/visits/VisitCard';
import { Visit } from '@/api/localStore';
import { useOdooAuth } from '@/lib/OdooAuthContext';

const PERIOD_FILTERS = [
  { key: 'today', label: 'اليوم' },
  { key: 'tomorrow', label: 'غداً' },
  { key: 'week', label: 'هذا الأسبوع' },
  { key: 'all', label: 'الكل' },
];

const STATUS_FILTERS = [
  { key: 'all', label: 'الكل' },
  { key: 'planned', label: 'مخططة' },
  { key: 'active', label: 'جارية' },
  { key: 'completed', label: 'مكتملة' },
  { key: 'cancelled', label: 'ملغاة' },
  { key: 'missed', label: 'مفقودة' },
];

const OUTCOME_FILTERS = [
  { key: 'all', label: 'الكل' },
  { key: 'order', label: 'مع طلب' },
  { key: 'collection', label: 'مع تحصيل' },
  { key: 'no_sale', label: 'بدون نتيجة' },
  { key: 'not_available', label: 'مشكلة' },
];

export default function Visits() {
  const { odooSession } = useOdooAuth();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // list | timeline

  const loadVisits = useCallback(async () => {
    setLoading(true);
    // Fetch a broad set once (ascending by date); period/date filtering is
    // done client-side so "today" and "tomorrow" are exact single days.
    const data = await Visit.filter({}, 'visit_date', 300);
    setVisits(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadVisits(); }, [loadVisits]);

  const handleStartVisit = (visit) => {
    navigate(`/visits/new?visit_id=${visit.id}&customer_id=${visit.customer_id}&customer_name=${encodeURIComponent(visit.customer_name)}`);
  };

  // Local (Cairo +2h) day keys for precise day matching.
  const nowLocal = new Date(Date.now() + 2 * 60 * 60 * 1000);
  const dayKey = (d) => d.toISOString().split('T')[0];
  const todayD = dayKey(nowLocal);
  const tomorrowD = dayKey(new Date(nowLocal.getTime() + 86400000));
  const weekAgoD = dayKey(new Date(nowLocal.getTime() - 7 * 86400000));
  const dayOf = (v) => {
    const m = String(v.visit_date || '').match(/(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
  };

  const periodMatch = (v) => {
    if (dateFilter) return dayOf(v) === dateFilter;
    if (period === 'today') return dayOf(v) === todayD;
    if (period === 'tomorrow') return dayOf(v) === tomorrowD;
    if (period === 'week') return dayOf(v) >= weekAgoD;
    return true; // all
  };

  // Apply period + status + outcome filters, sorted chronologically (soonest first).
  const filtered = visits
    .filter(v => {
      const statusOk = statusFilter === 'all' || v.status === statusFilter;
      const outcomeOk = outcomeFilter === 'all'
        || v.outcome === outcomeFilter
        || (v.outcome === 'order_collection' && (outcomeFilter === 'order' || outcomeFilter === 'collection'));
      return periodMatch(v) && statusOk && outcomeOk;
    })
    .sort((a, b) => new Date(b.check_in_time || b.visit_date) - new Date(a.check_in_time || a.visit_date));

  // Group by date for timeline view (earliest day first).
  const grouped = filtered.reduce((acc, v) => {
    const d = dayOf(v);
    if (!acc[d]) acc[d] = [];
    acc[d].push(v);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-bold text-foreground">الزيارات</h1>
          <div className="flex items-center gap-2">
            <div className="flex bg-muted rounded-xl p-0.5">
              <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-card shadow' : ''}`}>
                <List className="w-4 h-4 text-foreground" />
              </button>
              <button onClick={() => setViewMode('timeline')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'timeline' ? 'bg-card shadow' : ''}`}>
                <Clock className="w-4 h-4 text-foreground" />
              </button>
            </div>
            {odooSession?.role !== 'rep' && (
              <Button onClick={() => navigate('/visits/plan')} size="sm" variant="outline" className="rounded-xl gap-1.5">
                <ClipboardList className="w-4 h-4" />تخطيط
              </Button>
            )}
            <Button onClick={() => navigate('/visits/new')} size="sm" className="bg-primary rounded-xl gap-1.5">
              <Plus className="w-4 h-4" />زيارة
            </Button>
          </div>
        </div>

        {/* Period filter */}
        <div className="flex gap-1.5 px-4 pb-2 items-center flex-wrap">
          {PERIOD_FILTERS.map(f => (
            <button key={f.key} onClick={() => { setPeriod(f.key); setDateFilter(''); }}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${(!dateFilter && period === f.key) ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
              {f.label}
            </button>
          ))}
          <div className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 border transition-all ${dateFilter ? 'border-primary bg-primary/5' : 'border-border bg-muted'}`}>
            <Clock className={`w-3.5 h-3.5 ${dateFilter ? 'text-primary' : 'text-muted-foreground'}`} />
            <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)}
              className="bg-transparent text-xs font-semibold text-foreground focus:outline-none w-[7.5rem]" />
            {dateFilter && (
              <button onClick={() => setDateFilter('')} className="text-muted-foreground hover:text-red-500">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Status filter */}
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar px-4 pb-2">
          {STATUS_FILTERS.map(f => (
            <button key={f.key} onClick={() => { setStatusFilter(f.key); if (!['all', 'completed'].includes(f.key)) setOutcomeFilter('all'); }}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 transition-all ${statusFilter === f.key ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'}`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Outcome filter */}
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar px-4 pb-2.5">
          {OUTCOME_FILTERS.map(f => (
            <button key={f.key} onClick={() => { setOutcomeFilter(f.key); if (f.key !== 'all' && !['all', 'completed'].includes(statusFilter)) setStatusFilter('all'); }}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 transition-all ${outcomeFilter === f.key ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-transparent text-muted-foreground border border-border'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Today summary — show only when viewing today */}
        {period === 'today' && !dateFilter && !loading && (() => {
          const todays = visits.filter(v => dayOf(v) === todayD);
          return todays.length > 0 ? <TodaySummaryCard visits={todays} /> : null;
        })()}

        {loading ? <LoadingState /> : filtered.length === 0 ? (
          <EmptyState icon={MapPin} title="لا توجد زيارات"
            description={period === 'today' ? 'ابدأ زيارتك الأولى اليوم' : 'لا توجد زيارات في هذه الفترة'}
            action={<Button onClick={() => navigate('/visits/new')} className="bg-primary rounded-xl gap-2"><Plus className="w-4 h-4" />زيارة جديدة</Button>}
          />
        ) : (
          <AnimatePresence>
            {viewMode === 'list' ? (
              <motion.div key="list" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                {filtered.map((v, i) => (
                  <motion.div key={v.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                    <VisitCard visit={v} onStartVisit={handleStartVisit} showRep={odooSession?.role !== 'rep'} />
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              /* Timeline view */
              <motion.div key="timeline" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                {sortedDates.map(date => (
                  <div key={date}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-xs font-bold text-muted-foreground px-2 bg-background">
                        {new Date(date).toLocaleDateString('ar-EG', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    <div className="relative mr-4">
                      {/* Vertical line */}
                      <div className="absolute top-0 bottom-0 right-0 w-0.5 bg-border" />
                      <div className="space-y-4">
                        {grouped[date].sort((a, b) => new Date(b.check_in_time || b.visit_date) - new Date(a.check_in_time || a.visit_date)).map(v => {
                          const statusDot = v.status === 'completed' ? 'bg-emerald-500' : v.status === 'active' ? 'bg-blue-500' : v.status === 'planned' ? 'bg-amber-400' : 'bg-gray-300';
                          return (
                            <div key={v.id} className="flex items-start gap-4 pr-4">
                              <div className={`w-3 h-3 rounded-full flex-shrink-0 mt-2 -mr-[22px] border-2 border-background ${statusDot}`} />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs text-muted-foreground font-medium">
                                    {new Date(v.check_in_time || v.visit_date).toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}
                                    {v.duration_minutes > 0 && ` — ${v.duration_minutes} دق`}
                                  </span>
                                </div>
                                <VisitCard visit={v} onStartVisit={handleStartVisit} showRep={odooSession?.role !== 'rep'} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}